/*
 * Live-Modus der SPA (Feature-Dokument §4.5/§5).
 *
 * Solange eine Anlage angezeigt wird, hält dieser Store eine Live-Sitzung und
 * klopft im vom Server genannten Takt. Der Field-Client publiziert dann jede
 * Wertänderung statt nur die, die durch den CoV-Filter kommen.
 *
 * Der Store hängt an denselben drei Punkten wie das Werte-Abo im
 * `signalStore` — 0→1 abonniert, letztes 1→0 kündigt, Reconnect erneuert —
 * und nicht an einer Seitenkomponente. Grund: es gibt mehrere Seiten, die
 * Anlagenwerte zeigen (PlantSchemaPage, HmiPlantPage, PlantChartsPage), und
 * ein Live-Modus, der an einer davon hängt, wäre auf den anderen aus.
 *
 * DREI DINGE, DIE HIER ABSICHTLICH SO SIND:
 *
 * 1. Der Heartbeat verlängert, er startet nicht neu. Kommt von `ExtendLive`
 *    ein `null`, ist die Sitzung zu Ende.
 *
 * 2. Nach einem `null` wird nur dann neu geöffnet, wenn die harte Grenze noch
 *    nicht erreicht ist. Sonst wäre die harte Grenze wirkungslos: ein
 *    vergessener Tab, der bei jedem Ende brav eine neue Sitzung aufmacht,
 *    hält den Client für immer im Live-Modus — genau das, was
 *    `HardExpiresAt` verhindern soll. `hardExpiresAt` steht im Ticket, damit
 *    diese Unterscheidung möglich ist: liegt es noch in der Zukunft, war das
 *    `null` ein Neustart der API oder ein Abbruch und ein neuer Versuch ist
 *    richtig; ist es erreicht, war es die Grenze und wir hören auf.
 *
 * 3. Ein Fehler beim Start ist nicht fatal. Ohne Live-Modus zeigt die Seite
 *    weiter Werte, nur eben die gefilterten. Eine Anlagenseite, die wegen
 *    eines fehlgeschlagenen Live-Starts nichts mehr anzeigt, wäre der
 *    teurere Fehler.
 */

import { signal, type Signal } from '@preact/signals';
import { extendLive, startLive, stopLive, type LiveSessionTicket } from './hubClient';

/** Was die UI über den Live-Modus einer Anlage wissen kann. */
export type LiveStatus =
  /** Kein Live-Modus angefordert. */
  | 'off'
  /** Angefordert, Antwort steht aus. */
  | 'starting'
  /** Läuft, Heartbeat unterwegs. */
  | 'live'
  /** Die harte Grenze ist erreicht; ein Neuladen der Seite startet neu. */
  | 'expired'
  /** Start fehlgeschlagen. Die Seite zeigt weiter gefilterte Werte. */
  | 'failed';

interface LiveEntry {
  sessionId: string | null;
  /** Handle des Heartbeat-Intervalls. */
  timer: number | null;
  /** Hartes Ende der laufenden Sitzung, ms seit Epoche. */
  hardExpiresAtMs: number;
  /** Läuft gerade ein Start? De-dupliziert gleichzeitige Aufrufe. */
  starting: boolean;
  status: Signal<LiveStatus>;
}

const entries = new Map<string, LiveEntry>();

function entryFor(plantId: string): LiveEntry {
  let entry = entries.get(plantId);
  if (!entry) {
    entry = {
      sessionId: null,
      timer: null,
      hardExpiresAtMs: 0,
      starting: false,
      status: signal<LiveStatus>('off'),
    };
    entries.set(plantId, entry);
  }
  return entry;
}

/** Reaktiver Live-Status einer Anlage, für eine Anzeige in der Kopfzeile. */
export function liveStatus(plantId: string): Signal<LiveStatus> {
  return entryFor(plantId).status;
}

function clearTimer(entry: LiveEntry): void {
  if (entry.timer !== null) {
    window.clearInterval(entry.timer);
    entry.timer = null;
  }
}

function applyTicket(plantId: string, entry: LiveEntry, ticket: LiveSessionTicket): void {
  entry.sessionId = ticket.sessionId;
  entry.hardExpiresAtMs = Date.parse(ticket.hardExpiresAt);
  entry.status.value = 'live';

  clearTimer(entry);

  // Der Takt kommt aus dem Ticket. Untergrenze 5 s, damit eine kaputte
  // Server-Konfiguration (heartbeatSeconds: 0) nicht in eine Endlosschleife
  // von Aufrufen kippt.
  const periodMs = Math.max(5, ticket.heartbeatSeconds) * 1000;

  entry.timer = window.setInterval(() => {
    void beat(plantId, entry);
  }, periodMs);
}

async function beat(plantId: string, entry: LiveEntry): Promise<void> {
  const sessionId = entry.sessionId;
  if (sessionId === null) return;

  let ticket: LiveSessionTicket | null = null;
  try {
    ticket = await extendLive(sessionId);
  } catch (err) {
    // Ein einzelner verlorener Heartbeat ist kein Ereignis: die TTL ist ein
    // Vielfaches des Takts, der nächste Schlag holt es auf. Deshalb hier
    // kein Neustart und kein Statuswechsel.
    console.warn(`[liveStore] ExtendLive(${plantId}) fehlgeschlagen`, err);
    return;
  }

  if (ticket !== null) {
    entry.sessionId = ticket.sessionId;
    entry.hardExpiresAtMs = Date.parse(ticket.hardExpiresAt);
    return;
  }

  // Die Sitzung ist zu Ende. Jetzt die eine Unterscheidung, die zählt.
  clearTimer(entry);
  entry.sessionId = null;

  const hardGrenzeErreicht =
    Number.isFinite(entry.hardExpiresAtMs) && Date.now() >= entry.hardExpiresAtMs;

  if (hardGrenzeErreicht) {
    // Nicht neu öffnen. Sonst hätte die harte Grenze keine Wirkung.
    entry.status.value = 'expired';
    return;
  }

  // Kein hartes Ende, also war es ein Neustart der API oder ein Abbruch —
  // ein neuer Versuch ist richtig.
  entry.status.value = 'starting';
  await open(plantId, entry);
}

async function open(plantId: string, entry: LiveEntry): Promise<void> {
  if (entry.starting) return;
  entry.starting = true;
  try {
    const ticket = await startLive(plantId);
    applyTicket(plantId, entry, ticket);
  } catch (err) {
    // Nicht fatal: ohne Live-Modus zeigt die Seite weiter gefilterte Werte.
    console.warn(`[liveStore] StartLive(${plantId}) fehlgeschlagen`, err);
    entry.status.value = 'failed';
    entry.sessionId = null;
    clearTimer(entry);
  } finally {
    entry.starting = false;
  }
}

/**
 * Live-Modus für eine Anlage anfordern. Idempotent — ein zweiter Aufruf bei
 * laufender Sitzung tut nichts.
 *
 * Nach einem `expired` bewusst wirkungslos: die harte Grenze gilt, bis die
 * Seite neu geladen wird (`resetLive`).
 */
export function acquireLive(plantId: string): void {
  const entry = entryFor(plantId);

  if (entry.status.value === 'expired') return;
  if (entry.sessionId !== null || entry.starting) return;

  entry.status.value = 'starting';
  void open(plantId, entry);
}

/**
 * Live-Modus beenden (Anlagenwechsel, Tab versteckt, letzter Betrachter weg).
 *
 * Das `StopLive` an den Server ist die Beschleunigung, nicht die Bedingung:
 * geht es verloren, läuft die Sitzung nach ihrer TTL ab und der Field-Client
 * fällt von selbst zurück.
 */
export function releaseLive(plantId: string): void {
  const entry = entries.get(plantId);
  if (!entry) return;

  clearTimer(entry);

  const sessionId = entry.sessionId;
  entry.sessionId = null;
  if (entry.status.value !== 'expired') {
    entry.status.value = 'off';
  }

  if (sessionId !== null) {
    void stopLive(sessionId);
  }
}

/**
 * Nach einem Hub-Reconnect: die Verbindung war zu, und der Server hat die
 * Live-Sitzungen dieser Verbindung beim Disconnect geschlossen. Diese
 * Funktion wirft die tote SessionId weg, damit der nachfolgende
 * `acquireLive` (aus `ensureSubscribed`) eine neue Sitzung öffnet, statt mit
 * `ExtendLive` gegen eine Id zu klopfen, die es nicht mehr gibt.
 *
 * <b>Öffnet selbst nichts.</b> Das Abo muss zuerst wieder stehen: ein
 * Live-Modus ohne Werte-Abo wäre ein Field-Client, der jede Änderung sendet,
 * ohne dass sie jemand sieht.
 *
 * Kein `StopLive` — die Verbindung war weg, der Server hat schon aufgeräumt.
 */
export function invalidateLiveAfterReconnect(plantId: string): void {
  const entry = entries.get(plantId);
  if (!entry) return;

  clearTimer(entry);
  entry.sessionId = null;
  entry.starting = false;
  if (entry.status.value !== 'expired') {
    entry.status.value = 'off';
  }
}

/**
 * Setzt eine an der harten Grenze beendete Anlage zurück, damit ein
 * ausdrücklicher Wunsch des Benutzers wieder Live starten kann.
 *
 * Bewusst getrennt von <see cref="acquireLive"/>: automatisch aufgerufen
 * wäre es die Umgehung der harten Grenze.
 */
export function resetLive(plantId: string): void {
  const entry = entries.get(plantId);
  if (!entry) return;
  if (entry.status.value === 'expired') {
    entry.status.value = 'off';
  }
}

/** Nur für Tests: alle Sitzungen vergessen. */
export function __resetAllLiveForTests(): void {
  for (const entry of entries.values()) {
    clearTimer(entry);
  }
  entries.clear();
}
